import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { applyReadyBundleInBackground, checkForUpdate, getPreviewChannel, isFetchNetworkError, isOfflineDownloadError, notifyAppReady, type OtaCheckTrigger } from '../lib/ota'
import { isForgedManifestUrl } from '../lib/otaManifest'
import { Sentry } from '../lib/sentry'
import { log } from '../lib/logger'
import { RESTORE_PATH_KEY } from '../lib/webViewRestart'
import { installOtaSwapGuards, releaseOtaSwapHolds } from '../lib/otaSafeToSwap'

const CHECK_TIMEOUT_MS = 10_000
const PREVIEW_POLL_MS = 15_000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('OTA check timed out')), ms),
    ),
  ])
}

let checkInFlight = false

function runCheck(trigger: OtaCheckTrigger) {
  if (checkInFlight) return
  checkInFlight = true
  void withTimeout(checkForUpdate(trigger), CHECK_TIMEOUT_MS)
    .catch(err => {
      log('ota', 'check failed', { trigger, error: err instanceof Error ? err.message : String(err) })
      // A dropped connection is retried by the next resume or launch check,
      // and a failed preview poll by the next tick.
      if (trigger === 'poll') return
      // A resume check can fire before the WebView's network is back up; skip it
      // once the retry has failed too. Launch checks still report it, so a
      // systemic failure (e.g. Storage CORS) surfaces.
      if (trigger === 'resume' && isFetchNetworkError(err)) return
      // Logged by fetchManifest ('manifest rejected'); not reported (#673).
      if (isForgedManifestUrl(err)) return
      if (err instanceof Error && err.message !== 'OTA check timed out' && !isOfflineDownloadError(err)) {
        Sentry.captureException(err)
      }
    })
    .finally(() => { checkInFlight = false })
}

// Marks the running bundle stable on mount, then races a manifest check against
// a 10s timeout so slow networks don't block the rest of the app. Errors surface
// to Sentry but never propagate — a stale bundle is fine; users get the update
// on the next launch with better connectivity.
//
// Festival-goers can keep the app alive for days without a cold start, so every
// return to the foreground checks again, and every trip to the background swaps
// in a bundle a check downloaded (see checkForUpdate). While a preview is
// pinned, the channel is also polled in the foreground.
export function useOTACheck() {
  const navigate = useNavigate()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    // After an OTA reload, restore the route the user was on before the update
    const restorePath = localStorage.getItem(RESTORE_PATH_KEY)
    if (restorePath) {
      localStorage.removeItem(RESTORE_PATH_KEY)
      log('ota', 'restoring path after update', { path: restorePath })
      navigate(restorePath, { replace: true })
    }

    installOtaSwapGuards()
    // notifyAppReady clears capgo's next() slot, which a launch apply fills.
    void notifyAppReady()
      .catch(err => Sentry.captureException(err))
      .finally(() => runCheck('launch'))

    // The pin only changes across a bundle swap, which restarts the app.
    const previewPinned = getPreviewChannel() !== null
    let poll: ReturnType<typeof setInterval> | undefined
    function startPoll() {
      if (previewPinned && !poll) poll = setInterval(() => runCheck('poll'), PREVIEW_POLL_MS)
    }
    function stopPoll() {
      clearInterval(poll)
      poll = undefined
    }
    startPoll()

    // iOS reports isActive: false on willResignActive — Control Center, the
    // notification shade, a permission alert — with the app still on screen, so
    // it swaps on `pause` (didEnterBackground). Android's appStateChange
    // already comes from onStop.
    const swapOnPause = Capacitor.getPlatform() === 'ios'
    const swap = () => void applyReadyBundleInBackground(Date.now())

    const subs: { remove: () => void }[] = []
    let disposed = false
    const keep = (h: { remove: () => void }) => {
      if (disposed) h.remove()
      else subs.push(h)
    }
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        releaseOtaSwapHolds()
        runCheck('resume')
        startPoll()
      } else {
        stopPoll()
        if (!swapOnPause) swap()
      }
    }).then(keep)
    if (swapOnPause) void App.addListener('pause', swap).then(keep)
    return () => {
      disposed = true
      stopPoll()
      subs.forEach(h => h.remove())
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- registers app-lifetime listeners once on mount
}
